/**
 * Fuente unica de verdad de los planes.
 *
 * Tanto la UI (para deshabilitar botones y pintar medidores) como las Cloud
 * Functions (para hacerlos cumplir de verdad) leen de aqui. Si un limite se
 * duplica en otro sitio, tarde o temprano divergen y el cliente ve un numero
 * distinto del que el servidor aplica.
 */

export const PLAN_IDS = ['free', 'pro', 'business', 'comped'] as const;
export type PlanId = (typeof PLAN_IDS)[number];

export interface PlanLimits {
  /** Codigos QR activos simultaneos. */
  readonly codes: number;
  /**
   * Escaneos por mes natural. Al agotarlos, el codigo DEJA DE REDIRIGIR.
   *
   * Por eso los planes de pago no tienen tope: un QR impreso que deja de
   * funcionar a mitad de mes no lo sufre quien nos paga, lo sufre su cliente
   * delante de la mesa, y el recuerdo que le queda es del producto. Servir una
   * redireccion nos cuesta practicamente nada —el destino va en cache—, asi
   * que el tope solo existiria para forzar el pago, cobrandolo con la
   * reputacion.
   *
   * En el gratuito si hay tope, porque es una prueba y porque sin el seriamos
   * un acortador de enlaces gratis para cualquiera.
   */
  readonly scansPerMonth: number;
  /** Dias que se conservan los eventos de escaneo en crudo. */
  readonly analyticsRetentionDays: number;
  /**
   * Negocios propios: cuantos espacios separados puede tener una misma
   * persona, cada uno con sus codigos, su equipo y su plan.
   *
   * Existe para quien lleva los codigos de varios clientes desde una sola
   * cuenta, que es un uso de agencia y por tanto de pago. Sin este tope una
   * cuenta gratuita se multiplica a si misma: cinco negocios son cinco planes
   * gratuitos, con sus codigos y sus escaneos cada uno.
   */
  readonly workspaces: number;
  /**
   * Personas con acceso a la cuenta, incluida la propietaria.
   *
   * Las cifras son las de un negocio real y no las de un organigrama: hasta el
   * mas pequeno suele ser dos —quien manda y quien hace los carteles—, asi que
   * el gratuito trae dos y la funcion existe desde el primer dia. Inflar el
   * tope superior no vende nada: quien de verdad necesita veinticinco accesos
   * no esta mirando esta pagina, y al resto le dice que el producto no es para
   * ellos.
   *
   * Las invitaciones pendientes ocupan plaza. Si no, cinco invitaciones sobre
   * un plan de dos plazas se convierten en cinco miembros en cuanto las
   * aceptan, y el tope no habria servido de nada.
   */
  readonly teamMembers: number;
  /** Reglas de destino por dispositivo / idioma / pais / A-B. */
  readonly dynamicRouting: boolean;
  /** Dominio propio para los enlaces cortos. */
  readonly customDomain: boolean;
  /** Creacion masiva e importacion CSV. */
  readonly bulkCreate: boolean;
  /** Personalizacion visual avanzada (logo, formas, marcos). */
  readonly customDesign: boolean;
  /** Exportacion de la analitica en CSV. */
  readonly csvExport: boolean;
}

export interface Plan {
  readonly id: PlanId;
  readonly name: string;
  /**
   * Precio mensual en CENTAVOS de dolar. `null` = no se vende publicamente.
   *
   * En dolares y no en pesos, que es lo que habia antes. La razon no es de
   * gusto: quien cobra es Lemon Squeezy y liquida en dolares. Si la pagina
   * dijera pesos y el cargo saliera en dolares convertidos, los dos numeros se
   * separarian solos cada dia que se mueve la tasa, y el precio anunciado
   * dejaria de ser el precio cobrado. Un solo numero en una sola moneda es la
   * unica version que no miente.
   *
   * Tiene su coste y conviene no olvidarlo: un comprador colombiano vera un
   * cargo internacional, y muchas tarjetas debito de alla no salen del pais.
   * Ese hueco lo tapa Wompi cuando se añada, no este archivo.
   *
   * En centavos y no en dolares con decimales porque el dolar tiene exponente
   * 2 en la norma ISO 4217 y porque las pasarelas cobran en la unidad minima:
   * guardar 9.99 en coma flotante es como se pierde un centavo en una factura.
   */
  readonly monthlyPriceUsdCents: number | null;
  /** Si aparece en la pagina publica de precios. */
  readonly publiclyListed: boolean;
  readonly limits: PlanLimits;
}

export const UNLIMITED = Number.POSITIVE_INFINITY;

export const PLANS: Readonly<Record<PlanId, Plan>> = {
  free: {
    id: 'free',
    name: 'Gratis',
    monthlyPriceUsdCents: 0,
    publiclyListed: true,
    limits: {
      codes: 1,
      scansPerMonth: 1_000,
      analyticsRetentionDays: 15,
      workspaces: 1,
      teamMembers: 1,
      dynamicRouting: false,
      customDomain: false,
      bulkCreate: false,
      customDesign: false,
      csvExport: false,
    },
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    monthlyPriceUsdCents: 900, // 9 USD
    publiclyListed: true,
    limits: {
      codes: 50,
      scansPerMonth: UNLIMITED,
      analyticsRetentionDays: 365,
      workspaces: 3,
      teamMembers: 5,
      dynamicRouting: true,
      customDomain: false,
      bulkCreate: true,
      customDesign: true,
      csvExport: true,
    },
  },
  business: {
    id: 'business',
    name: 'Negocios',
    monthlyPriceUsdCents: 2_900, // 29 USD
    publiclyListed: true,
    limits: {
      codes: 500,
      scansPerMonth: UNLIMITED,
      analyticsRetentionDays: 730,
      workspaces: 10,
      teamMembers: 15,
      dynamicRouting: true,
      // Todavia no esta construido. Se activara cuando exista: prometerlo en
      // la pagina de precios seria vender algo que el cliente no va a recibir.
      customDomain: false,
      bulkCreate: true,
      customDesign: true,
      csvExport: true,
    },
  },
  /**
   * Cuenta de cortesia: uso libre, sin cobro y sin limites.
   * Solo la asigna un superadmin. La UI del cliente oculta cualquier aviso de
   * consumo o de mejora de plan cuando la organizacion esta en este estado.
   */
  comped: {
    id: 'comped',
    name: 'Cortesia',
    monthlyPriceUsdCents: null,
    publiclyListed: false,
    limits: {
      codes: UNLIMITED,
      scansPerMonth: UNLIMITED,
      analyticsRetentionDays: UNLIMITED,
      workspaces: UNLIMITED,
      teamMembers: UNLIMITED,
      dynamicRouting: true,
      customDomain: true,
      bulkCreate: true,
      customDesign: true,
      csvExport: true,
    },
  },
};

/**
 * Meses que se pagan al contratar un ano entero. Dos salen gratis.
 *
 * Es una constante y no un precio anual escrito a mano en cada plan para que
 * no puedan divergir: si manana el mensual sube, el anual sube con el. Y se
 * expresa en meses pagados, no en un porcentaje, porque es lo que se le dice
 * al cliente —"dos meses gratis"— y asi el numero de la pagina y la frase de
 * la pagina salen del mismo sitio.
 */
export const MESES_QUE_SE_PAGAN = 10;
export const MESES_DEL_ANO = 12;
export const MESES_GRATIS_AL_ANO = MESES_DEL_ANO - MESES_QUE_SE_PAGAN;

/**
 * Lo que se cobra de una vez al contratar un ano, en centavos.
 *
 * Es el precio mensual por los meses que se pagan, exacto y sin redondear.
 * Esta es la cifra que viaja a la pasarela, asi que es la que manda.
 *
 * Antes se hacia al reves —se redondeaba un "equivalente mensual" y se
 * multiplicaba por doce— y eso dejaba el total a merced del redondeo: con
 * 29 USD al mes salia 288 en vez de 290. El total es la promesa que se le hace
 * al cliente ("pagas diez meses"), de modo que el total se calcula exacto y lo
 * que se redondea es el adorno.
 */
export function yearlyTotalUsdCents(plan: Plan): number | null {
  if (plan.monthlyPriceUsdCents === null) return null;
  return plan.monthlyPriceUsdCents * MESES_QUE_SE_PAGAN;
}

/**
 * El "al mes" que se enseña en el ciclo anual.
 *
 * Es un numero de escaparate, no un cobro: nadie paga esto doce veces. Por eso
 * se deriva del total y nunca al reves, y por eso puede llevar decimales
 * —24,17 al mes— sin que eso descuadre nada.
 */
export function yearlyMonthlyEquivalentUsdCents(plan: Plan): number | null {
  const total = yearlyTotalUsdCents(plan);
  return total === null ? null : Math.round(total / MESES_DEL_ANO);
}

/**
 * Centavos a texto, en dolares.
 *
 * Sin decimales cuando es una cifra redonda —"$9", no "$9.00"— porque el
 * precio de escaparate se lee mejor corto; con dos cuando los hace falta.
 * Vive aqui para que la portada y el panel no inventen cada uno su formato.
 */
export function formatUsd(cents: number): string {
  const dolares = cents / 100;
  return Number.isInteger(dolares) ? `$${dolares}` : `$${dolares.toFixed(2)}`;
}

export const DEFAULT_PLAN: PlanId = 'free';

/**
 * Cuantos negocios propios admite alguien, visto el conjunto de sus planes.
 *
 * Se queda con el mas generoso: si pagas Pro en uno, no tendria sentido que el
 * gratuito de al lado te limitara a uno. Sin ninguno todavia, manda el plan
 * por defecto.
 */
export function maxWorkspacesFor(planes: readonly PlanId[]): number {
  if (planes.length === 0) return PLANS[DEFAULT_PLAN].limits.workspaces;
  return planes.reduce(
    (mayor, id) => Math.max(mayor, PLANS[id].limits.workspaces),
    0,
  );
}

export function isPlanId(value: unknown): value is PlanId {
  return typeof value === 'string' && (PLAN_IDS as readonly string[]).includes(value);
}

/**
 * Limites efectivos de una organizacion: los del plan, con los ajustes
 * puntuales que un superadmin haya podido fijar por encima.
 */
export function resolveLimits(
  plan: PlanId,
  overrides?: Partial<PlanLimits> | null,
): PlanLimits {
  const base = PLANS[plan].limits;
  if (!overrides) return base;
  return { ...base, ...overrides };
}

/** Una organizacion de cortesia nunca ve medidores ni avisos de upgrade. */
export function hidesBillingUi(plan: PlanId): boolean {
  return PLANS[plan].monthlyPriceUsdCents === null;
}

export function isUnlimited(limit: number): boolean {
  return !Number.isFinite(limit);
}

/** Planes que se muestran en la pagina publica de precios, en orden. */
export function listedPlans(): readonly Plan[] {
  return PLAN_IDS.map((id) => PLANS[id]).filter((p) => p.publiclyListed);
}
